import React, { useEffect, useState } from 'react';
import { getSites, getThemes, getAllThemesCached, updateTheme } from '../api';
import { RefreshCw, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import Flag from '../components/Flag';

const timeAgo = (d) => {
  const s = Math.floor((Date.now() - new Date(d)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function Themes() {
  const [sites, setSites]           = useState([]);
  const [themes, setThemes]         = useState([]);
  const [selectedSite, setSelectedSite]       = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [statusFilter, setStatusFilter]       = useState('all'); // all | updates | uptodate
  const [loading, setLoading]       = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalSites, setTotalSites]   = useState(0);
  const [updating, setUpdating]     = useState({});

  const [lastSynced, setLastSynced] = useState(null);

  // Instant load from the DB cache; Sync does a live refresh per site.
  const loadCached = async () => {
    try {
      const [s, rows] = await Promise.all([getSites(), getAllThemesCached()]);
      setSites(s);
      setTotalSites(s.length);
      setThemes(rows.map(t => ({ ...t, country: t.country || 'Other' })));
      const newest = rows.reduce((m, r) => r.last_synced > m ? r.last_synced : m, '');
      setLastSynced(newest || null);
      if (rows.length === 0 && s.length > 0) syncLive(s); // first run: cache empty
    } catch (err) { console.error(err); }
  };

  const syncLive = async (knownSites) => {
    setLoading(true);
    setLoadedCount(0);
    try {
      const s = knownSites || sites.length ? (knownSites || sites) : await getSites();
      setSites(s);
      setTotalSites(s.length);
      await Promise.all(s.map(site =>
        getThemes(site.id)
          .then(ts => {
            setThemes(prev => [
              ...prev.filter(x => x.site_id !== site.id),
              ...ts.map(t => ({ ...t, site_id: site.id, site_name: site.name, country: site.country||'Other' })),
            ]);
          })
          .catch(err => console.warn(`Themes load failed for ${site.name}:`, err))
          .finally(() => setLoadedCount(c => c + 1))
      ));
      setLastSynced(new Date().toISOString());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadCached(); }, []);

  const reload = () => syncLive();

  const handleUpdate = async (siteId, slug, name) => {
    setUpdating(u=>({...u,[`${siteId}-${slug}`]:true}));
    try {
      await updateTheme(siteId, slug);
      toast.success(`${name} updated!`);
      setThemes(prev => prev.map(t => t.site_id===siteId&&t.slug===slug?{...t,update_available:false}:t));
    } catch(err) { toast.error(err?.error||'Update failed'); }
    finally { setUpdating(u=>({...u,[`${siteId}-${slug}`]:false})); }
  };

  const countries = ['all', ...new Set(sites.map(s=>s.country||'Other'))];

  const sitesForCountry = selectedCountry==='all'
    ? sites
    : sites.filter(s=>(s.country||'Other')===selectedCountry);

  const effectiveSite = sitesForCountry.find(s=>s.id===parseInt(selectedSite)) ? selectedSite : 'all';

  const baseFiltered = themes
    .filter(t => selectedCountry==='all' || t.country===selectedCountry)
    .filter(t => effectiveSite==='all' || t.site_id===parseInt(effectiveSite));

  const updatesCount  = baseFiltered.filter(t=>t.update_available).length;
  const uptodateCount = baseFiltered.length - updatesCount;
  const pending = updatesCount;

  const filtered = baseFiltered.filter(t =>
    statusFilter==='all' ? true : statusFilter==='updates' ? t.update_available : !t.update_available
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Themes</h1>
          <p>{loading
            ? `Syncing… ${loadedCount}/${totalSites} sites checked`
            : `${pending} update${pending!==1?'s':''} available${lastSynced ? ` · synced ${timeAgo(lastSynced)}` : ''}`}</p>
        </div>
        <div className="header-actions">
          <select style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,padding:'6px 10px',fontSize:12,color:'var(--text2)'}}
            value={selectedCountry} onChange={e=>setSelectedCountry(e.target.value)}>
            <option value="all">🌍 All countries</option>
            {countries.filter(c=>c!=='all').map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,padding:'6px 10px',fontSize:12,color:'var(--text2)'}}
            value={effectiveSite} onChange={e=>setSelectedSite(e.target.value)}>
            <option value="all">All sites</option>
            {sitesForCountry.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <Button className="btn" onClick={reload} loading={loading}><RefreshCw size={13}/> {loading?'Syncing...':'Sync'}</Button>
          <Button className="btn primary" onClick={async()=>{for(const t of baseFiltered.filter(t=>t.update_available)) await handleUpdate(t.site_id,t.slug,t.name);}} disabled={!pending}><Zap size={13}/> Update all ({pending})</Button>
        </div>
      </div>
      <div className="page-content">
        <div className="seg" style={{ marginBottom: 14 }}>
          <button className={statusFilter==='all'?'active':''} onClick={()=>setStatusFilter('all')}>
            All <span className="seg-count">{baseFiltered.length}</span>
          </button>
          <button className={statusFilter==='updates'?'active':''} onClick={()=>setStatusFilter('updates')}>
            <span className="seg-dot" style={{background:'var(--amber)'}}></span> Updates available <span className="seg-count">{updatesCount}</span>
          </button>
          <button className={statusFilter==='uptodate'?'active':''} onClick={()=>setStatusFilter('uptodate')}>
            <span className="seg-dot" style={{background:'var(--green)'}}></span> Up to date <span className="seg-count">{uptodateCount}</span>
          </button>
        </div>
        <div className="card">
          <table className="tbl">
            <thead><tr><th>Theme</th><th>Site</th><th>Country</th><th>Current</th><th>Latest</th><th>Status</th><th>Active</th><th>Action</th></tr></thead>
            <tbody>
              {!filtered.length&&<tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>
                {loading ? `Loading themes… ${loadedCount}/${totalSites} sites checked` : 'No themes found.'}
              </td></tr>}
              {filtered.map((t,i)=>(
                <tr key={i}>
                  <td className="bold">{t.name}</td>
                  <td style={{fontSize:11,color:'var(--text3)'}}>{t.site_name}</td>
                  <td><span style={{display:'inline-flex',alignItems:'center',gap:7}}><Flag country={t.country}/> <span style={{fontSize:11,color:'var(--text3)'}}>{t.country}</span></span></td>
                  <td>{t.version}</td>
                  <td style={{color:t.update_available?'var(--green-text)':'var(--text3)'}}>{t.new_version||t.version}</td>
                  <td><span className={`badge ${t.update_available?'amber':'green'}`}>{t.update_available?'Update available':'Up to date'}</span></td>
                  <td><span className={`badge ${t.active?'blue':'gray'}`}>{t.active?'Active':'Inactive'}</span></td>
                  <td>{t.update_available
                    ?<Button className="btn sm success" onClick={()=>handleUpdate(t.site_id,t.slug,t.name)} loading={updating[`${t.site_id}-${t.slug}`]}><RefreshCw size={11}/> Update</Button>
                    :<span style={{fontSize:11,color:'var(--text3)'}}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
